import React from 'react';

import cx from 'classnames';

import { Action } from '~shared/components/ActionMenu/ActionMenu';
import { Badge } from '~shared/components/Badge';
import { Divider } from '~shared/components/Divider';
import { IconCheckmark } from '~shared/components/icons/standard';
import { proCheck } from '~teacher/util/proCheck';

type Props<T> = Action<T> & {
  data: Array<T>;
  closePopper: () => void;
  // When true the menu lays out as a 2-column grid (checkmark | content) and
  // this item renders into those subgrid tracks. The grid container lives in
  // `ActionMenu`, so the checkmark column is defined once and every row aligns.
  showCheckGutter?: boolean;
};

export function ActionMenuItem<T>({
  disabled,
  searchKey,
  className,
  color,
  action,
  data,
  title,
  description,
  divider,
  heading,
  selected,
  closePopper,
  paywallCallback,
  showProBadge,
  showCheckGutter,
}: Props<T>) {
  const isDisabled = disabled?.({ data }) ?? false;
  const isSelected = selected?.({ data }) ?? false;
  const dataKey = isDisabled ? undefined : searchKey;
  const hasPaidPlan = proCheck.hasPaidPlan();
  const onClick = () => {
    if (paywallCallback && !hasPaidPlan) {
      paywallCallback();
      return;
    }
    if (isDisabled) return;
    return action({ data, close: closePopper });
  };

  const resolvedTitle = typeof title === 'function' ? title({ data }) : title;
  const titleRow = (
    <div className="flex items-center gap-2">
      {resolvedTitle}
      {showProBadge && !hasPaidPlan ? (
        <Badge className="ml-2" tone="pro">
          PRO
        </Badge>
      ) : null}
    </div>
  );
  const resolvedDescription =
    typeof description === 'function' ? description({ data }) : description;
  const descriptionEl =
    resolvedDescription !== undefined && resolvedDescription !== null ? (
      <p className="text-grey-800 text-xs whitespace-normal mt-2 font-sans">
        {resolvedDescription}
      </p>
    ) : null;

  // ── Grid layout: menus that show a checkmark column ──────────────────────
  // The row is a subgrid spanning the parent's [checkmark | content] tracks,
  // so the content column starts at the same x on every row and the heading
  // lands in the content column (col-start-2). The checkmark fills its cell
  // only on the selected row without shifting anything.
  if (showCheckGutter) {
    return (
      <>
        {divider ? (
          <div tabIndex={-1} className="col-span-2 py-1">
            <Divider />
          </div>
        ) : null}
        {heading ? (
          <div
            tabIndex={-1}
            className="col-start-2 pt-4 pb-2 pr-4 text-xs font-700 tracking-wider uppercase text-grey-900"
          >
            {heading}
          </div>
        ) : null}
        <div
          tabIndex={0}
          data-key={dataKey}
          onClick={onClick}
          className={cx(
            'col-span-2 grid grid-cols-subgrid items-start rounded-1 outline-none hocus:bg-cyan-50',
            isDisabled ? 'cursor-default opacity-50' : 'cursor-pointer',
            className,
            color
          )}
        >
          <div className="flex justify-center leading-8 py-2 pl-2" aria-hidden>
            {isSelected ? (
              <IconCheckmark className="inline-block align-middle" />
            ) : null}
          </div>
          <div className="py-2 pr-4">
            <div
              className={cx(
                'text-base font-sans leading-8',
                color ?? 'text-blue-900'
              )}
              style={{ color: color ?? '' }}
            >
              {titleRow}
            </div>
            {descriptionEl}
          </div>
        </div>
      </>
    );
  }

  // ── Default flex layout (unchanged for every other menu) ─────────────────
  const componentClassName = cx(
    'flex w-full px-4 py-2 whitespace-no-wrap font-sans text-base leading-8 text-left border-0 rounded-1 bg-white text-blue-900 outline-none hocus:bg-cyan-50',
    { ['cursor-default']: isDisabled, ['cursor-pointer']: !isDisabled },
    { ['opacity-50']: isDisabled, ['opacity-100']: !isDisabled },
    className,
    color
  );
  return (
    <>
      {divider ? (
        <div tabIndex={-1} className={'w-full py-1'}>
          <Divider />
        </div>
      ) : null}
      <div className="px-2 py-1">
        <div
          // tabIndex={disabled ? (disabled({ data }) ? -1 : 0) : 0} <- will prevent disabled items from being tabbed
          tabIndex={0}
          // prevent disabled items from being searchable via keyboard
          data-key={dataKey}
          // ensure that a focus state exists on disabled items, so the user knows the location of focus
          className={componentClassName}
          onClick={onClick}
        >
          <div>
            <div
              className={cx('text-base font-sans', color ?? 'text-blue-900')}
              style={{ color: color ?? '' }}
            >
              {titleRow}
            </div>
            {descriptionEl}
          </div>
        </div>
      </div>
    </>
  );
}
